import type { FormSchemaResolved } from '#layers/block-engine/public';
import type { BuiltinConnectorDefinition, ConnectorDefinition, ConnectorNotice, ConnectorSupportLevel } from '#layers/data-model/types/data-connectors/define';
import type { ConnectorAvailableSince } from '#layers/data-model/utils/connector-availability';
import type { TranslationPlaceholder } from '#layers/i18n/public';
import { defineConnector } from '#layers/data-model/types/data-connectors/define';
import { resolveConnectorAvailableSince } from '#layers/data-model/utils/connector-availability';
import { applyAwsIamFields, AuthType } from './aws-iam';

/**
 * Configuration for a branded connector preset built on top of a base connector.
 */
export interface BrandedConnectorOptions<TOptions extends Record<string, any>> {
	id: string;
	name: TranslationPlaceholder;
	description: TranslationPlaceholder;
	icon?: string;
	iconForeground: string;
	iconBackground: string;
	keywords?: string[];
	availableSince?: ConnectorAvailableSince;
	supportLevel?: ConnectorSupportLevel;
	docsUrl?: string;
	/** Tweaks to inherited base fields without re-declaring the form. */
	overrides?: {
		connectionStringPlaceholder?: string;
		defaultPort?: number;
	};
	/**
	 * Authentication methods this preset offers. The AWS IAM auth chooser and
	 * fields appear only when {@link AuthType.AwsIam} is included.
	 *
	 * @default [AuthType.Password]
	 */
	authMethods?: AuthType[];
	/** Contextual notices surfaced in the connector's options form. */
	notices?: ConnectorNotice<TOptions>[];
}

/**
 * Create a frontend-only branded connector preset that extends a base connector.
 *
 * Branded presets remap the base connector's options form (connection-string
 * placeholder, default port) and declare contextual notices, while inheriting
 * `schema`, `onOptionsChange`, and `transformOptions` from the base.
 *
 * A preset with a base options form always owns a wrapper getter so AWS IAM
 * controls remain opt-in through `authMethods`, even without field overrides.
 */
export function createBrandedConnector<TExtends extends BuiltinConnectorDefinition<any, any>>(
	base: TExtends,
	options: BrandedConnectorOptions<TExtends['~options']>,
): BuiltinConnectorDefinition<TExtends['~options'], TExtends> {
	const { overrides, authMethods } = options;
	const showAwsIam = authMethods?.includes(AuthType.AwsIam) ?? false;

	return defineConnector({
		id: options.id,
		name: options.name,
		description: options.description,
		type: base.type,
		extends: base,
		icon: options.icon,
		iconForeground: options.iconForeground,
		iconBackground: options.iconBackground,
		keywords: options.keywords,
		availableSince: () => resolveBrandedAvailableSince(base, options.availableSince),
		supportLevel: options.supportLevel,
		docsUrl: options.docsUrl,
		notices: options.notices,
		...(base.options || overrides || showAwsIam
			? {
					options: ({ form, values, context }) => {
						let fields: FormSchemaResolved<TExtends['~options']> = context.base?.resolvedOptions ?? [];

						if (overrides?.connectionStringPlaceholder != null) {
							const placeholder = overrides.connectionStringPlaceholder;

							fields = fields.map((field) => {
								if ('name' in field && field.name === 'connectionString' && 'props' in field) {
									return { ...field, props: { ...field.props, placeholder } };
								}

								return field;
							});
						}

						if (overrides?.defaultPort != null) {
							const defaultPort = overrides.defaultPort;

							fields = fields.map((field) => {
								if ('name' in field && field.name === 'port') {
									return { ...field, default: () => defaultPort };
								}

								return field;
							});
						}

						if (showAwsIam) {
							return form(applyAwsIamFields(fields, values));
						}

						return form([...fields]);
					},
				}
			: {}),
	});
}

function resolveBrandedAvailableSince(
	base: ConnectorDefinition<any, any>,
	availableSince: ConnectorAvailableSince | undefined,
) {
	const baseAvailableSince = resolveConnectorAvailableSince(base.availableSince);
	if (!baseAvailableSince) return;

	const presetAvailableSince = resolveConnectorAvailableSince(availableSince);
	if (!presetAvailableSince) return baseAvailableSince;

	if (baseAvailableSince > presetAvailableSince) return baseAvailableSince;
	return presetAvailableSince;
}
